import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Loader from "./components/Loader/Loader";
import LoginPage from "./pages/LoginPage";
import AuthRedirect from "./components/AuthRedirect";
import ProtectedRoute from "./components/ProtectedRoute";
import Matter from "./pages/Matter/Matter"
import ResponsiveMatterDetails from "./pages/MatterDetails/ResponsiveMatterDetails";
import MatterProcessing from "./pages/MatterProcessing/MatterProcessing";
import BriefReader from "./pages/BriefReader/BriefReader";
import { NotificationPage } from "./pages/Notification";
import Home from "./pages/Home/Home";
import SettingsPage from "./pages/Settings";
import TemplateEditor from "./pages/Settings/FileTemplates/TemplateEditor";
import TemplateStudio from "./pages/Settings/FileTemplates/studio/TemplateStudio";
import WordAddinPane from "./pages/WordAddin/WordAddinPane";
import WordAddinConnect from "./pages/WordAddin/WordAddinConnect";
import SearchPage from "./pages/Search";
import ReviewPage from "./pages/Review";
import MailIntake from "./pages/MailIntake/MailIntake";
import FeeNoticeQueue from "./pages/FeeNoticeQueue/FeeNoticeQueue";
import IntakeReview from "./pages/IntakeReview/IntakeReview";
import BoardAppeals from "./pages/BoardAppeals/BoardAppeals";
import Fax from "./pages/Fax/Fax";
import Inbox from "./pages/Inbox/Inbox";
import DocumentPage from "./pages/Document/DocumentPage";
import EmbeddedDocumentPage from "./pages/Document/EmbeddedDocumentPage";
import IntakePage from "./pages/Intake/IntakePage";
import BriefBank from "./pages/BriefBank/BriefBank";
import ArticlesBank from "./pages/ArticlesBank/ArticlesBank";
import MyFiles from "./pages/MyFiles/MyFiles";
import VaSubmissionsPage from "./pages/VaSubmissions/VaSubmissionsPage";
import SignaturesPage from "./pages/Signatures/SignaturesPage";
import EfolderPullsPage from "./pages/EfolderPulls/EfolderPullsPage";
import PersonalLinkLanding from "./pages/MyFiles/PersonalLinkLanding";
import SharedFolder from "./pages/SharedFolder/SharedFolder";
import PdfBuilder from "./pages/PdfBuilder/PdfBuilder";
import Help from "./pages/Help/Help";
import Partners from "./pages/Partners/Partners";
import ReferralsPage from "./pages/Referrals/ReferralsPage";
import { useRouteScrollCleanup } from "./utils/scrollCleanup";
import PageTitle from "./components/PageTitle";
import SessionKeeper from "./components/SessionKeeper/SessionKeeper";

// Determine which component to use based on environment
const getAuthComponent = () => {
  console.log('getAuthComponent', globalThis.location.hostname);
  const isLocalhost = globalThis.location.hostname === 'localhost';
  return isLocalhost ? LoginPage : AuthRedirect;
};

const isAuthenticated = true; // Change this to `false` to restrict access to Dashboard

// Component that handles route scroll cleanup - must be inside Router context
const RouteScrollHandler = () => {
  useRouteScrollCleanup();
  return null;
};

const AppRouter = () => {
  return (
    <Router>
      <RouteScrollHandler />
      <PageTitle />
      <SessionKeeper />
      <Routes>
        <Route path="/" element={React.createElement(getAuthComponent())} />
        <Route path="/home" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Home />} path="/home" />} />
        <Route path="/matter" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Matter />} path="/matter" />} />
        <Route path="/matter/:id/processing" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<MatterProcessing />} path="/matter" />} />
        <Route path="/matter/:id/brief/:jobId" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<BriefReader />} path="/matter" noLayout />} />
        {/* Must precede the /matter/:id/* catch-all. */}
        <Route path="/matter/:id/pdf-builder" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<PdfBuilder />} path="/matter" noLayout />} />
        <Route path="/matter/:id/*" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ResponsiveMatterDetails />} path="/matter" />} />
        <Route path="/notifications" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<NotificationPage />} path="/notifications" />} />
        <Route path="/search" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<SearchPage />} path="/notifications" />} />
        <Route path="/review" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ReviewPage />} path="/review" />} />
        <Route path="/mail-intake" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<MailIntake />} path="/mail-intake" />} />
        <Route path="/fee-notices" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<FeeNoticeQueue />} path="/fee-notices" />} />
        <Route path="/intake-review" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<IntakeReview />} path="/intake-review" />} />
        <Route path="/board-appeals" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<BoardAppeals />} path="/board-appeals" />} />
        <Route path="/inbox" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Inbox />} path="/inbox" />} />
        <Route path="/fax" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Fax />} path="/fax" />} />
        <Route path="/va-submissions" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<VaSubmissionsPage />} path="/va-submissions" />} />
        <Route path="/signatures" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<SignaturesPage />} path="/signatures" />} />
        <Route path="/efolder-pulls" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<EfolderPullsPage />} path="/efolder-pulls" />} />
        <Route path="/referrals" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ReferralsPage />} path="/referrals" />} />
        <Route path="/brief-bank" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<BriefBank />} path="/brief-bank" />} />
        <Route path="/articles-bank" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<ArticlesBank />} path="/articles-bank" />} />
        <Route path="/my-files" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<MyFiles />} path="/my-files" />} />
        <Route path="/my-files/shared/:token" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<PersonalLinkLanding />} path="/my-files" />} />
        {/* Public, unauthenticated landing page for external folder shares — deliberately
            outside ProtectedRoute/Layout: the recipient never logs in. */}
        <Route path="/shared-folder/:token" element={<SharedFolder />} />
        {/* The WHG Docs panel inside Word (Office add-in) and its sign-in dialog; both carry their own auth. */}
        <Route path="/word-addin" element={<WordAddinPane />} />
        <Route path="/word-addin/connect" element={<WordAddinConnect />} />
        <Route path="/inbox/:documentId" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Inbox />} path="/inbox" />} />
        {/* A single document by id, for links from outside the app (the Salesforce intake console). */}
        <Route path="/document/:documentId" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<DocumentPage />} path="/document" />} />
        {/* The intake file: a client who has not signed yet, shown like a matter (whgdocs-intake-files-design.md). */}
        <Route path="/intake/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<IntakePage />} path="/matter" />} />
        {/* Framed inside the Salesforce intake console; the pass in the address is the credential, so no ProtectedRoute and no Layout. */}
        <Route path="/embed/document/:token" element={<EmbeddedDocumentPage />} />
        {/* Article slugs contain a slash ("submit-to-va/overview"), so the splat carries the whole path. */}
        <Route path="/help" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Help />} path="/help" />} />
        <Route path="/help/*" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Help />} path="/help" />} />
        <Route path="/partners" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<Partners />} path="/partners" />} />
        <Route path="/settings" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<SettingsPage />} isAdminOnly={true} />} />
        <Route path="/settings/templates/:formstackId" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<TemplateStudio />} isAdminOnly={true} noLayout path="/" />} />
        <Route path="/template-editor/:formstackId" element={<ProtectedRoute isAuthenticated={isAuthenticated} element={<TemplateEditor />} isAdminOnly={true} noLayout path="/" />} />
        <Route path="*" element={<Loader />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;